import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { catchError, filter, map, takeUntil } from 'rxjs/operators';
import {
  addStudioScene,
  deleteProjectFailure,
  deleteProjectSuccess,
  removeStudioScene,
  saveScene,
  saveStudioResources,
  setChangeTookPlace,
  setCurrentStudioSceneIndex,
  setSceneDurations,
  setStudioState,
} from './studio.actions';
import {
  selectChangeTookPlace,
  selectCurrentProject,
  selectCurrentStudioScene,
  selectCurrentStudioSceneId,
  selectStudioImages,
  selectStudioScenes,
  selectStudioVideoInstances,
  selectStudioVideos,
} from './studio.selector';
import {
  StudioScene,
  StudioSceneCell,
  StudioImage,
  StudioVideo,
  VideoInstance,
} from './studio.types';
import { StudioSceneShape } from '../../shape/shapes/general/root/studio-scene-shape';
import { cloneDeep, delay, isEqual } from 'lodash';
import { ShapeService } from '../../shape/shape.service';
import { TextTopologyProcessService } from './text/services/text-topology-process.service';
import { TextDescriptorGeneratorService } from './text/services/text-descriptor-generator.service';
import { TextAnimationGeneratorService } from './text/services/text-animation-generator.service';
import { TextAnimationStep } from '../../animation/text-animation.service';
import gsap from 'gsap';
import { StudioLayoutService } from './layout';
import {
  StudioTextShape,
  StudioTextShapeDescriptor,
} from './text/studio-text-shape';
import { VideoShape } from '../../shape/shapes/base/video-shape';
import { CanvasService } from '../../../services/canvas/canvas.service';
import { StudioProject } from './studio.reducer';
import { BackgroundService } from './background/background.service';
import { HttpService, RequestType } from '../../../store/http/http.service';
import { ErrorService } from '../../../services/error/error.service';
import { SVGParseService } from '../../svg/svg-parse.service';
import { currentOrganisation } from '../../../organisation/organisation.selector';
import { Organisation } from '../../../organisation/organisation.interface';
import { VideoPlayerService } from './video/video.service';

@Injectable({ providedIn: 'root' })
export class StudioService implements OnDestroy {
  private changeTookPlace = 0;
  private destroy$ = new Subject<void>();
  currentSceneId: string | undefined;
  images: Record<string, StudioImage>;
  videos: Record<string, StudioVideo>;

  videoInstances: Record<string, VideoInstance>;
  get currentSceneShape() {
    return this.studioSceneShapes[this.currentSceneId];
  }

  get currentSceneIndex() {
    return this.currentSceneShape?.positionIndex;
  }
  currentScenes: StudioScene[];
  currentScene: StudioScene;
  currentProject: StudioProject;
  currentOrganisation: Organisation;
  constructor(
    private store: Store,
    private httpService: HttpService,
    public shapeService: ShapeService,
    public textDescriptorGeneratorService: TextDescriptorGeneratorService,
    public textAnimationGeneratorService: TextAnimationGeneratorService,
    public textTopologyProcessService: TextTopologyProcessService,
    private layoutService: StudioLayoutService,
    private readonly backgroundService: BackgroundService,
    private readonly canvasService: CanvasService,
    private readonly svgParseService: SVGParseService,
    private readonly errorService: ErrorService,

    public readonly videoPlayerService: VideoPlayerService,
  ) {
    console.log('studio-service > constructor', this.svgParseService);
    this.store.select(selectCurrentProject).subscribe(project => {
      this.currentProject = project;
    });
    this.store.select(selectCurrentStudioScene).subscribe(scene => {
      this.currentScene = cloneDeep(scene);
    });

    this.store.select(selectStudioImages).subscribe(images => {
      this.images = images;
    });

    this.shapeService.cs.generalEventSubscribe('currentX', currentX => {
      this.scrollCanvasToSceneX(currentX);
    });

    this.shapeService.cs.generalEventSubscribe('currentY', currentY => {
      this.scrollCanvasToY(currentY);
    });

    this.canvasService.generalEventSubscribe('studio-start-animation', () => {
      console.log('studio-start-animation');
      const timeline = this.currentSceneShape.gsapAnimate();
      timeline.eventCallback('onComplete', () => {
        console.log('on-complete > animation-finished');
        this.canvasService.generalEventEmit('animation-finished');
      });
    });

    this.shapeService.cs.generalEventSubscribe('fit-canvas-updated', scale => {
      if (!this.scenesByPositionIndex?.length) {
        return;
      }
      // this.minv
      // console.log('fit-canvas-updated', scale);
      if (this.shapeService.cs.isPortrait) {
        this.shapeService.cs.minCanvasX =
          -(this.scenesByPositionIndex.length - 1) *
          this.shapeService.cs.sceneWithGap *
          scale;
        // this..map(shape => {
        //   shape.setPositionByCurrentX(this.currentX);
        // });
      } else {
        this.shapeService.cs.minCanvasY =
          -(this.scenesByPositionIndex.length - 1) *
          this.shapeService.cs.sceneWithGap *
          scale;
      }
    });

    this.store
      .select(selectChangeTookPlace)
      .pipe(takeUntil(this.destroy$))
      .subscribe(changeTookPlace => {
        if (!this.changeTookPlace) {
          this.changeTookPlace = changeTookPlace;
          setTimeout(() => {
            this.changeTookPlace = null;
            this.store.dispatch(saveStudioResources());
          }, 10_000);
        }
      });

    combineLatest([
      this.store.select(selectStudioScenes),
      this.store.select(selectCurrentProject),
      this.store.select(currentOrganisation),
    ])
      .pipe(
        takeUntil(this.destroy$),
        filter(
          ([scenes, org, project]) => !!scenes.length && !!org && !!project,
        ),
      )
      .subscribe(([scenes, project, organisation]) => {
        this.currentProject = project;
        this.currentOrganisation = organisation;
        if (!scenes?.length) {
          return;
        }
        if (isEqual(scenes, this.currentScenes)) {
          return;
        }
        this.currentScenes = cloneDeep(scenes);
        try {
          this.initStudioSceneShapes(this.currentScenes);
        } catch (error) {
          // -- //
          this.store.dispatch(setStudioState({ value: 'error' }));
        }
      });

    this.store.select(selectStudioScenes).subscribe(scenes => {});

    // fire first -> wait

    this.store
      .select(selectCurrentStudioSceneId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(sceneId => {
        this.currentSceneId = sceneId;
      });

    this.store.select(selectStudioVideos).subscribe(videos => {
      this.videos = videos;
    });

    this.store.select(selectStudioVideoInstances).subscribe(videoInstances => {
      // console.log('currentVideoInstances', videoInstances);
      this.videoInstances = videoInstances;
      // -- //
    });
  }
  studioSceneShapes: Record<string, StudioSceneShape> = {};
  initialized = false;
  sceneDurations: number[] = [];

  // canvasDy can be between -cs.sceneWithGap and -cs.sceneWithGap
  sceneLocalTime = 0;

  get currentFontFamily() {
    return (
      this.currentProject?.fontFamily ||
      this.currentOrganisation?.fontFamily ||
      'Goldman'
    );
  }

  deleteProject(projectID: string, orgID: string): Observable<string> {
    return this.httpService
      .requestCall({
        type: RequestType.DELETE,
        path: `project/${projectID}`,
        headers: {
          'organisation-id': orgID,
        },
      })
      .pipe(map(() => projectID));
  }

  get currentAnimationTime() {
    return (
      (this.currentSceneShape?.getAbsoluteStartTime() || 0) +
      this.sceneLocalTime
    );
  }

  canvasDy = 0;
  // Time is an absolute value
  setTo(sceneId: string, time: number) {
    // const scene = this.studioSceneShapes[sceneId];
    // const absoluteTime = scene.getAbsoluteStartTime() + time;
    // const scene = this.studioSceneShapes[sceneId];
    const absoluteTime = this.currentSceneShape.getAbsoluteStartTime() + time;
    // this.currentSceneShape.getAbsoluteStartTime(); //
    this.resetAnimation();
    this.mainTimeline?.seek(absoluteTime);
  }

  regenerateBackground() {
    this.backgroundService.regenerateBackground(
      this.currentSceneShape,
      this.shapeService.cs.isPortrait,
    );
  }

  goToScene(index: number) {
    this.store.dispatch(setCurrentStudioSceneIndex({ value: index }));
    this.scrollCanvasToY(
      -index *
        this.shapeService.cs.sceneWithGap *
        this.shapeService.cs.fitScale,
    );
    this.resetAnimation();
  }

  currentX = 0;
  currentY = 0;

  scrollCanvasToX(currentX: number) {
    this.currentX = currentX;

    const _currentX = currentX / this.shapeService.cs.fitScale;

    this.scenesByPositionIndex.forEach(shape => {
      if (!shape) {
        return;
      }
      shape.setPositionByCurrentX(_currentX);
    });
    // return;
  }

  addNext(
    baseTimeline: gsap.core.Timeline,
    timeline: gsap.core.Timeline,
    delay = 0,
    overlap = 0,
  ) {
    const offset = overlap ? `>-=${overlap}` : `+=${delay || 0}`;
    baseTimeline.add(timeline, offset);
  }

  addParalell(
    baseTimeline: gsap.core.Timeline,
    timeline: gsap.core.Timeline,
    delay = 0,
    overlap = 0,
  ) {
    const offset = overlap ? `-=${overlap}` : delay || 0;
    baseTimeline.add(timeline, offset);
  }

  getItemDuration(n: number, completeDuration: number, overlap = 0) {
    if (!overlap) {
      return (completeDuration || 1) / n;
    } else {
      return (completeDuration || 1) / ((1 - overlap) * (n - 1) + 1);
    }
  }

  addAnimationChain(
    baseTimeline: gsap.core.Timeline,
    timelines: gsap.core.Timeline[],
    params: {
      completeDuration: number;
      overlap?: number;
      delay?: number;
    },
  ) {
    const { completeDuration, overlap = 0, delay = 0 } = params;
    const n = Math.ceil(timelines.length);
    let itemDuration: number;
    if (!overlap) {
      itemDuration = (completeDuration || 1) / n;
    } else {
      itemDuration = (completeDuration || 1) / ((1 - overlap) * (n - 1) + 1);
    }
    timelines.forEach((timeline, index) => {
      baseTimeline.add(timeline, index * itemDuration * (1 - overlap));
    });
  }

  scrollCanvasToY(currentY: number) {
    this.currentY = currentY;
    // -- //
    const _currentY = currentY / this.shapeService.cs.fitScale;
    this.scenesByPositionIndex.forEach(shape => {
      if (!shape) {
        return;
      }
      shape.setPositionByCurrentY(_currentY);
    });
  }

  scrollCanvasToSceneX(currentX: number) {
    // -- // -- //
    this.currentX = currentX;
    const _currentX = currentX / this.shapeService.cs.fitScale;

    this.scenesByPositionIndex.forEach(shape => {
      if (!shape) {
        return;
      }
      shape.setPositionByCurrentX(_currentX);
    });
  }

  scenesByPositionIndex: StudioSceneShape[];
  private async initStudioSceneShapes(scenes: StudioScene[]) {
    // Remove old shapes that are no longer in the scenes list
    // console.log('initStudioSceneShapes', scenes);
    if (scenes.length) {
      this.shapeService.cs.minCanvasY =
        -(scenes.length - 1) *
        this.shapeService.cs.sceneWithGap *
        this.shapeService.cs.fitScale;
    }
    const currentIds = new Set(scenes.map(s => s.id));
    Object.keys(this.studioSceneShapes).forEach(id => {
      if (!currentIds.has(id)) {
        this.studioSceneShapes[id].remove();
        delete this.studioSceneShapes[id];
      }
    });

    // Create or update shapes for each scene
    this.scenesByPositionIndex = scenes
      .sort((a, b) => a.positionIndex - b.positionIndex)
      .map(scene => {
        if (this.studioSceneShapes[scene.id]) {
          // Update existing shape
          this.studioSceneShapes[scene.id].updateScene(scene);
        } else {
          // Create new shape // -- //
          const shape = new StudioSceneShape(this, scene);
          this.studioSceneShapes[scene.id] = shape;
        }
        return this.studioSceneShapes[scene.id];
      });

    // console.log('wait-for-load.promise');
    await Promise.all(
      this.scenesByPositionIndex.map(shape => shape.loadPromise),
    );

    // show the last 'add-new-scene'
    setTimeout(() => {
      this.scenesByPositionIndex[this.scenesByPositionIndex.length - 1].show();
    }, 1_000);

    // Generate array of animation durations for,  each scene
    this.sceneDurations = this.scenesByPositionIndex
      .filter(shape => shape.scene.id !== 'add-after-last')
      .map(shape => {
        return shape.calcAnimationTime();
      });

    this.store.dispatch(setSceneDurations({ durations: this.sceneDurations }));
    // console.log('sceneDurarions', this.sceneDurarions); // -- //
    // console.log('Scene animation durations:', this.sceneAnimationDurations); // -- //

    if (!this.initialized) {
      this.initialized = true;
      this.store.dispatch(setStudioState({ value: 'loaded' }));
    }
  }

  addSceneBefore() {
    // move the ones with positionIndex >= currentSceneIndex up by one
    this.currentSceneShape.prev?.disablePositionUpdateForPrevs();
    this.shapeService.cs.moveUp(false);
    setTimeout(() => {
      this.currentY -=
        this.shapeService.cs.sceneWithGap * this.shapeService.cs.fitScale;
      this.currentSceneShape.prev?.enablePositionUpdateForPrevs();
      this.store.dispatch(addStudioScene({ ref: 'before' }));
      this.store.dispatch(setChangeTookPlace());
    }, 600);
  }

  addLastScene() {
    this.store.dispatch(addStudioScene({ ref: 'after' }));
  }

  addSceneAfter() {
    this.currentSceneShape.next?.disablePositionUpdateForNexts();
    this.shapeService.cs.moveDown();
    setTimeout(() => {
      this.currentSceneShape.next?.enablePositionUpdateForNexts();
      this.store.dispatch(addStudioScene({ ref: 'after' }));
    }, 600);
  }

  deleteScene(sceneId: string) {
    const isLast = this.currentSceneShape.next == null;
    if (isLast) {
      this.shapeService.cs.moveUp();
    }

    const isFirst = this.currentSceneShape.prev == null;
    if (isFirst) {
      this.shapeService.cs.moveDown();
      setTimeout(() => {
        this.shapeService.cs.currentY = 0;
        this.currentY = 0;
        this.scenesByPositionIndex.map(scene => {
          scene.updateDefaultPosition();
        });
      }, 600);
    }

    this.store.dispatch(removeStudioScene({ sceneId }));
    this.store.dispatch(setChangeTookPlace());
  }
  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  regenerateAnimation() {
    const {
      texts: text,
      images,
      videoInstances: videos,
    } = this.currentSceneShape.scene;
    // -- // -- //
    const onlyText = text && !images?.length && !videos?.length;
    // -- // -- //
    if (onlyText) {
      const { 'text:0': cell } = this.currentSceneShape.shapeCells;
      const animationStep = cell.regenerateAnimation();
      return;
    }
  }

  resetToStart() {
    // this.store.dispatch(setCurrentStudioSceneIndex({ value: 0 }));
    // this.scenesByPositionIndex.forEach(scene => {
    //   scene.reset();
    // }); // -- //
    this.currentSceneShape.resetAnimation();
  }

  /**
   * Navigate to the next scene
   */
  goToNextScene() {
    const currentIndex = this.currentSceneShape?.positionIndex ?? 0;
    const nextIndex = Math.min(
      currentIndex + 1,
      this.scenesByPositionIndex.length - 1,
    );
    this.store.dispatch(setCurrentStudioSceneIndex({ value: nextIndex }));
  }

  /**
   * Navigate to the previous scene
   */
  goToPreviousScene() {
    const currentIndex = this.currentSceneShape?.positionIndex ?? 0;
    const prevIndex = Math.max(currentIndex - 1, 0);
    this.store.dispatch(setCurrentStudioSceneIndex({ value: prevIndex }));
  }

  /**
   * Navigate to the last scene
   */
  goToLastScene() {
    const lastIndex = this.scenesByPositionIndex.length - 1;
    this.store.dispatch(setCurrentStudioSceneIndex({ value: lastIndex }));
  }

  resetToLocalTime() {
    // this.mainTimeline.pause();
    // this.mainTimeline.seek(this.mainTimeline.duration());
  }

  mainTimeline: gsap.core.Timeline;
  animationIsPlaying = false;

  resetAnimation() {
    this.mainTimeline?.kill();
    this.mainTimeline = null;
    this.animationIsPlaying = false;
    this.sceneLocalTime = 0;
  }
  playPause(complete = false) {
    if (this.animationIsPlaying) {
      Object.values(this.currentSceneShape.shapes).filter(shape => {
        if (shape.getType() == 'video-shape') {
          (shape as VideoShape).onMasterPause();
        }
      });
      this.animationIsPlaying = false;
      this.mainTimeline.pause();
      return;
    }

    if (this.mainTimeline) {
      Object.values(this.currentSceneShape.shapes).filter(shape => {
        if (shape.getType() == 'video-shape') {
          (shape as VideoShape).onMasterResume();
        }
      });
      this.mainTimeline.play();
      this.animationIsPlaying = true;
      return;
    }

    this.animationIsPlaying = true;
    this.mainTimeline = gsap.timeline();

    if (!complete) {
      complete = this.shapeService.cs.isShiftPressed;
    }

    this.mainTimeline = this.currentSceneShape.gsapAnimate({
      complete,
    });

    this.mainTimeline.play();
    this.mainTimeline.eventCallback('onComplete', () => {
      console.log('onComplete');
      if (!complete) {
        return;
      }
      this.resetToStart();
      this.animationIsPlaying = false;
      this.sceneLocalTime = 0;
      this.mainTimeline = null;
    });
  }
  moveCurrentThree() {
    // this.currentSceneShape.moveCurrentThree();
  }

  // regenerateLayout() {
  // const {
  //   texts: text,
  //   images,
  //   videoInstances: videos,
  // } = this.currentSceneShape.scene;
  // const onlyText = text && !images?.length && !videos?.length;
  // if (onlyText) {
  //   const { 'text:0': cell } = this.currentSceneShape.shapeCells;
  //   cell.regenerateTextLayout();
  //   return;
  // }
  // const onlyImages = !text && images?.length && !videos?.length;
  // const textAndOneImage =
  //   text && (images?.length === 1 || videos?.length == 1);
  // if (textAndOneImage) {
  //   // -- //
  //   const topology = this.getTopology();
  //   switch (topology) {
  //     case 'cover':
  //       break;
  //     case 'diagonal':
  //       break;
  //     case 'row':
  //       const {
  //         cells,
  //         images,
  //         videoInstances: videos,
  //       } = this.currentSceneShape.scene;
  //       const textCell = cells['text:0'];
  //       const imageCell = cells[images[0] || videos[0]];
  //       // -- //
  //       const tmp = imageCell.x;
  //       imageCell.x = textCell.x;
  //       textCell.x = tmp;
  //       this.currentSceneShape.scene.cells = {
  //         [images[0] ? `image:0` : `video:0`]: imageCell,
  //         [`text:0`]: textCell,
  //       };
  //       this.currentSceneShape.initCells();
  //       this.currentSceneShape.save();
  //       break;
  //   }
  // }
  // const textAndOneVideo = text && !images?.length && videos?.length === 1;
  // -- //
  // }

  /**
   * Get animation for current scene - delegates to StudioAnimationService
   * @deprecated Use StudioAnimationService.getDefaultAnimation() directly
   */
  getAnimation() {
    return this.currentSceneShape?.scene?.animation;
  }

  getTopology() {
    return this.currentSceneShape.scene.topology || 'row';
  }

  textChanged(newText: string, index = 0) {
    if (!newText) {
      return;
    }

    const id = this.currentScene.texts[index];
    if (!id) {
      return;
    }
    (this.currentScene.shapes[id] as StudioTextShapeDescriptor).text = newText;

    (this.currentSceneShape.shapes[id] as StudioTextShape).update(
      this.currentScene.shapes[id] as StudioTextShapeDescriptor,
    );
    console.log('textChanged', this.currentScene.shapes[id]);
    this.saveScene();
  }

  saveScene() {
    this.store.dispatch(
      saveScene({
        value: cloneDeep(this.currentScene),
      }),
    );
  }

  addImage(id: string) {
    if (!this.currentSceneShape) {
      return;
    }
  }

  addVideo(videoInstance: VideoInstance) {
    // -- // -- // -- // -- // -- //
  }

  imageChanged(images: string[]) {
    this.currentSceneShape.scene.images = images;
    this.updateLayout();
    this.currentSceneShape.save();
  }

  videoChanged(videos: string[]) {
    this.currentSceneShape.scene.videoInstances = videos;
    // -- //
    this.currentSceneShape.addVideo('video:0', {
      videoInstanceId: videos[0],
      cell: {
        w: 0.5,
        h: 0.5,
        x: 0.5,
        y: 0,
      },
    });
  }

  updateLayout() {
    const { scene } = this.currentSceneShape;
    const { texts: text, images, videoInstances } = scene;

    const array: Array<{
      type: 'text' | 'image' | 'video';
      typeIndex: number;
      text?: string;
      image?: string;
      videoInstance?: string;
    }> = [];

    // Track separate indices for each type
    let textIndex = 0;
    let imageIndex = 0;
    let videoIndex = 0;

    // Handle text - now always an array
    if (text && text.length > 0) {
      text.forEach(textValue => {
        if (textValue) {
          array.push({ type: 'text', typeIndex: textIndex++, text: textValue });
        }
      });
    }

    if (images) {
      images.forEach(image => {
        array.push({ type: 'image', typeIndex: imageIndex++, image });
      });
    }

    if (videoInstances) {
      videoInstances.forEach(instance => {
        array.push({
          type: 'video',
          typeIndex: videoIndex++,
          videoInstance: instance,
        });
      });
    }

    const cnt = array.length;

    const cells = array.reduce(
      (obj, { type, typeIndex, text, image, videoInstance }, index) => {
        const key = `${type}:${typeIndex}`;
        obj[key] = {
          w: 1 / cnt,
          h: 1,
          x: index * (1 / cnt),
          y: 0,
          text,
          image,
          videoInstance,
        };
        return obj;
      },
      {} as Record<string, StudioSceneCell>,
    );

    this.currentSceneShape.scene.cells = cells;
    this.currentSceneShape.initShapes();
    this.currentSceneShape.save();
  }

  removeImage(index: number) {
    if (!this.currentSceneShape) {
      console.warn('No current scene shape');
      return;
    }
    // -- //
    this.currentSceneShape.removeImage(index);
  }

  removeVideo(index: number) {
    if (!this.currentSceneShape) {
      console.warn('No current scene shape');
      return;
    }
    // -- //
    this.currentSceneShape.removeVideo(index);
  }

  /**
   * Calculate the total animation duration for a scene
   */
  private calculateSceneAnimationDuration(scene: StudioScene): number {
    const { texts: text, images, videoInstances: videos } = scene;
    const onlyText = text && !images?.length && !videos?.length;

    if (onlyText) {
      // For text-only scenes, get duration from text animation
      const textCell = scene.cells?.['text:0'];
      if (textCell?.textAnimation) {
        return this.calculateTextAnimationDuration(textCell.textAnimation);
      }
      // Default text animation duration
      return 1;
    }

    // For scenes with images/videos, use default animation from StudioAnimationService
    const hasVideo = videos && videos.length > 0;
    const hasImage = images && images.length > 0;

    if (text && hasVideo) {
      // Text + Video: 1.5 (initial) + 1 (animation) = 2.5
      return 2.5;
    }

    if (text && hasImage) {
      // Text + Image: 1 (initial) + 1 (animation) = 2
      return 2;
    }

    // Default: no animation
    return 0;
  }

  /**
   * Calculate total duration of a TextAnimationStep including its chain
   */
  private calculateTextAnimationDuration(
    animation: TextAnimationStep | undefined,
  ): number {
    if (!animation) {
      return 0;
    }

    const baseDuration = animation.duration || 1;
    let nextDuration = 0;
    let paralellDuration = 0;

    // Calculate duration of next animation in chain
    if (animation.next) {
      nextDuration = this.calculateTextAnimationDuration(animation.next);
    }

    // Calculate duration of parallel animation
    if (animation.paralell) {
      if (Array.isArray(animation.paralell)) {
        // If multiple parallel animations, take the longest
        paralellDuration = Math.max(
          ...animation.paralell.map((p: TextAnimationStep) =>
            this.calculateTextAnimationDuration(p),
          ),
        );
      } else {
        paralellDuration = this.calculateTextAnimationDuration(
          animation.paralell,
        );
      }
    }

    // Total duration is base + next (sequential)
    // Parallel animations run at the same time as base, so we take the max
    return Math.max(baseDuration, paralellDuration) + nextDuration;
  }
}
